import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import { toggleActiveChat } from "@/pages/app/chat/store";
import { useDispatch } from "react-redux";
import useMobileMenu from "@/hooks/useMobileMenu";

const Navmenu = ({ menus }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showSubmenu, setShowSubmenu] = useState(null);
  const location = useLocation();
  const locationName = location.pathname.replace("/", "");
  const [mobileMenu, setMobileMenu] = useMobileMenu();
  const dispatch = useDispatch();

  const isLocationMatch = (targetLocation) => {
    return (
      locationName === targetLocation ||
      locationName.startsWith(`${targetLocation}/`)
    );
  };

  useEffect(() => {
    document.title = `MySway | ${locationName}`;
    dispatch(toggleActiveChat(false));
    if (mobileMenu) {
      setMobileMenu(false);
    }
  }, [location, mobileMenu, dispatch]);

  const handleMouseEnter = (i) => {
    setHoveredItem(i);
    if (menus[i].child) {
      setShowSubmenu(i);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
    setShowSubmenu(null);
  };

  return (
    <div className="flex flex-col space-y-1 md:space-y-2 px-2 overflow-y-auto scrollbar-hide">
      {menus
        .filter(item => !item.isHeadr) // Filter out headers in compact mode
        .map((item, i) => {
          const isActive = isLocationMatch(item.link) || 
            (item.child && item.child.some(child => isLocationMatch(child.childlink)));

          return (
            <div key={i} className="relative group">
              {/* Main Menu Item - Facebook Style */}
              {!item.child ? (
                <NavLink
                  to={item.link}
                  className={`
                    flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-300 relative
                    min-h-[60px] min-w-[60px] touch-manipulation
                    ${isActive 
                      ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 shadow-lg' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-slate-100'
                    }
                    hover:scale-105 hover:shadow-lg active:scale-95 group
                    md:hover:scale-105 
                  `}
                  onMouseEnter={() => handleMouseEnter(i)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Icon on top */}
                  <div className={`text-xl mb-1 transition-all duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>
                    <Icon icon={item.icon} />
                  </div>
                  
                  {/* Text below */}
                  <span className="text-[10px] font-medium text-center leading-tight px-1">
                    {item.title}
                  </span>

                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-blue-500 rounded-full"></div>
                  )}

                  {/* Badge */}
                  {item.badge && (
                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
                      {item.badge}
                    </div>
                  )}
                </NavLink>
              ) : (
                // Parent item with children - show flyout on hover
                <div
                  className={`
                    flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-300 cursor-pointer relative
                    min-h-[60px] min-w-[60px] touch-manipulation
                    ${isActive 
                      ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 shadow-lg' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-slate-100'
                    }
                    hover:scale-105 hover:shadow-lg active:scale-95 group
                    md:hover:scale-105
                  `}
                  onMouseEnter={() => handleMouseEnter(i)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Icon on top */}
                  <div className={`text-xl mb-1 transition-all duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>
                    <Icon icon={item.icon} />
                  </div>
                  
                  {/* Text below */}
                  <span className="text-[10px] font-medium text-center leading-tight px-1">
                    {item.title}
                  </span>

                  {/* Submenu indicator */}
                  <div className="absolute top-2 right-2 text-[8px] opacity-60">
                    <Icon icon="heroicons:chevron-right" />
                  </div>

                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              )}

              {/* Flyout Submenu */}
              {item.child && showSubmenu === i && (
                <div 
                  className="fixed left-[80px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-xl shadow-2xl py-2 min-w-[200px] z-[1001] transition-all duration-300"
                  style={{ 
                    top: `${80 + (i * 70)}px`,
                  }}
                  onMouseEnter={() => setShowSubmenu(i)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="px-3 py-2 border-b border-slate-200/30 dark:border-slate-700/30 mb-2">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {item.title}
                    </span>
                  </div>
                  
                  {item.child.map((childItem, j) => (
                    <NavLink
                      key={j}
                      to={childItem.childlink}
                      className={`
                        flex items-center px-4 py-3 text-sm transition-all duration-200 hover:bg-slate-100/80 dark:hover:bg-slate-800/80 mx-2 rounded-lg
                        ${isLocationMatch(childItem.childlink) 
                          ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10' 
                          : 'text-slate-700 dark:text-slate-300'
                        }
                      `}
                    >
                      {childItem.icon && (
                        <div className="mr-3 text-base">
                          <Icon icon={childItem.icon} />
                        </div>
                      )}
                      <span>{childItem.childtitle}</span>
                    </NavLink>
                  ))}
                </div>
              )}

              {/* Hover Tooltip for main items */}
              {hoveredItem === i && !item.child && (
                <div className="fixed left-[80px] bg-slate-900 text-white text-xs px-2 py-1 rounded-lg shadow-lg z-[1002] whitespace-nowrap pointer-events-none"
                     style={{ top: `${80 + (i * 70)}px` }}>
                  {item.title}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default Navmenu;